import { fabric } from 'fabric';
import CanvasObject from "./CanvasObject";
import Canvas from "./../Canvas";
import CanvasDebug from "../../CanvasDebug";
import polygonFunctions from "./../../helper/polygonFunctions";
import configuration from "../../configuration/Configuration";

class BoundingBox extends CanvasObject {

    /**
     * Minimum value for the position on the x-axis.
     * @type number
     */
    _minX;

    /**
     * Maximum value for the position on the x-axis.
     * @type number
     */
    _maxX;

    /**
     * Minimum value for the position on the y-axis.
     * @type number
     */
    _minY;

    /**
     * Maximum value for the position on the y-axis.
     * @type number
     */
    _maxY;

    /**
     * Create a BoundingBox.
     * @param {Canvas} canvas Instance of the canvas on which the object is displayed.
     * @param {number} minX Smallest value that can be defined for the property left.
     * @param {number} maxX Highest value that can be defined for the property left.
     * @param {number} minY Smallest value that can be defined for the property top.
     * @param {number} maxY Highest value that can be defined for the property top.
     */
    constructor(canvas, minX, maxX, minY, maxY) {
        super(canvas);

        this.initAllIsManipulable();

        this._id = 'boundingBox';
        this._minX = minX;
        this._maxX = maxX;
        this._minY = minY;
        this._maxY = maxY;
        this._zPosition = null;
    }

    /**
     * Returns a new FabricObject that can be added to the canvas.
     * @returns {Promise<fabric.Object>|Promise<Array<fabric.Object>>}
     */
    async createFabricObject() {

        const subjectPolygon = [{x: 0, y: 0}, { x: this.getCanvas().getWidth(), y: 0}, { x: this.getCanvas().getWidth(), y: this.getCanvas().getHeight()}, { x: 0, y: this.getCanvas().getHeight()}];
        const subtractPolygon = [
            [
                { x: this.getLeft() - 2, y: this.getTop() - 2 },
                { x: this.getLeft() + this.getWidth() + 2, y: this.getTop() - 2 },
                { x: this.getLeft() + this.getWidth() + 2, y: this.getTop() + this.getHeight() + 2 },
                { x: this.getLeft() - 2, y: this.getTop() + this.getHeight() + 2 }
            ]
        ];
        const polygon = new fabric.Polygon(
            polygonFunctions.combinePolygonArrays(subjectPolygon, polygonFunctions.sortPolygonArray(subtractPolygon, polygonFunctions.closetPoint(subjectPolygon[0], subtractPolygon))),
            { id: 'transparent', fill: 'white', fillRule: 'evenodd', selectable: false, opacity: 0.5, evented: false }
        );


        if (configuration.getConfig().boundingBox.visible === false && window['w2p_admin'] !== true) {
            return new fabric.Rect({
                id: this.getId(),
                height: this.getHeight() + 2,
                width: this.getWidth() + 2,
                top: this.getTop() - 2,
                left: this.getLeft() - 2,
                fill: 'transparent',
                stroke: 'transparent',
                strokeWidth: 2,
                selectable: false,
                preserveObjectStacking: true,
                evented: false
            });
        }

        return [
            new fabric.Rect({
                id: this.getId(),
                height: this.getHeight() + 2,
                width: this.getWidth() + 2,
                top: this.getTop() - 2,
                left: this.getLeft() - 2,
                fill: 'transparent',
                stroke: 'grey',
                strokeWidth: 2,
                strokeDashArray: [ 5 ],
                selectable: false,
                preserveObjectStacking: true,
                evented: false
            }),
            polygon
        ];

    }

    // Getter & Setter Methods

    setTop(value) {
        if ( this.getMinY() <= value && this.getMaxY() >= value ) {

            CanvasDebug.canvas.boundingBox.moveY('The BoundingBox has been moved along the Y-axis and the following objects are adjusted relative to it: ', this.getCanvas().getCanvasObjects().filter(canvasObject => canvasObject.getExceedsBoundingBox() === false));

            // Set new y-coordinate if the value is within the allowed range.
            super.setTop(value);

            // Each object inside the BoundingBox moves relative to the vertical BoundingBox.
            this.getCanvas().getCanvasObjects().forEach(canvasObject => {

                // if (! canvasObject.getExceedsBoundingBox())
                    canvasObject.setTop(
                        typeof canvasObject.getTopBoundingBox() === "number" ? value + canvasObject.getTopBoundingBox() : canvasObject.getTop()
                    );
            });

        }

        else throw 'The value for the property top of the BoundingBox must be within the range defined by min and max.';
    }

    setLeft(value) {
        if ( this.getMinX() <= value && this.getMaxX() >= value ) {

            CanvasDebug.canvas.boundingBox.moveX('The BoundingBox has been moved along the X-axis and the following objects are adjusted relative to it: ', this.getCanvas().getCanvasObjects().filter(canvasObject => canvasObject.getExceedsBoundingBox() === false));

            // Set new x-coordinate if the value is within the allowed range.
            super.setLeft(value);

            // Each object inside the BoundingBox moves relative to the vertical BoundingBox.
            this.getCanvas().getCanvasObjects().forEach(canvasObject => {

                if (! canvasObject.getExceedsBoundingBox())
                    canvasObject.setLeft(
                        canvasObject.getLeftBoundingBox() ? value + canvasObject.getLeftBoundingBox() : canvasObject.getLeft()
                    );
            });

        }

        else throw 'The value for the property left of the BoundingBox must be within the range defined by min and max.';
    }

    getMinX() {
        return this._minX;
    }

    getMaxX() {
        return this._maxX;
    }

    getMinY() {
        return this._minY;
    }

    getMaxY() {
        return this._maxY;
    }
}

export default BoundingBox;